import {
  BookMarked,
  Calendar,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  ShoppingBag,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../services/api';

const NAV = [
  { path: '/admin/dashboard', label: 'Visão Geral', Icon: LayoutDashboard },
  { path: '/admin/eventos', label: 'Eventos', Icon: Calendar },
  {
    path: '/admin/eventos-especiais',
    label: 'Eventos Extras',
    Icon: CalendarPlus,
  },
  { path: '/admin/inscricoes', label: 'Inscrições', Icon: ClipboardList },
  { path: '/admin/comprovantes', label: 'Comprovantes', Icon: FileCheck },
  { path: '/admin/assinantes', label: 'Assinantes', Icon: CreditCard },
  { path: '/admin/membros', label: 'Lily Members', Icon: Star },
  { path: '/admin/livros', label: 'Livros Lidos', Icon: BookMarked },
  { path: '/admin/produtos', label: 'Produtos', Icon: ShoppingBag },
  { path: '/admin/pedidos', label: 'Pedidos', Icon: Package },
];

export default function AdminSidebar({ collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(
    window.innerHeight < window.innerWidth,
  );

  function isActive(path) {
    if (path === '/admin/dashboard') return pathname === '/admin/dashboard';
    return pathname === path || pathname.startsWith(`${path}/`);
  }

  async function handleLogout() {
    try {
      await api.post('/auth/logout');
    } catch {}
    window.location.href = '/admin/login';
  }

  useEffect(() => {
    function handleOrientationChange() {
      const isNowLandscape = window.innerHeight < window.innerWidth;
      setIsLandscape(isNowLandscape);
      if (isNowLandscape) {
        setMobileOpen(false);
      }
    }
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, []);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  function handleNav(path) {
    navigate(path);
    setMobileOpen(false);
  }

  const SidebarContent = ({ isDesktop }) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className={`flex items-center gap-3 px-4 py-5 border-b border-white/10 ${collapsed && isDesktop ? 'justify-center px-2' : ''}`}
      >
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-wine to-wine-deep flex items-center justify-center text-white font-black text-base flex-shrink-0 shadow-sm">
          L
        </div>
        {(!collapsed || !isDesktop) && (
          <div className="min-w-0">
            <p className="font-gilroy text-sm font-extrabold uppercase tracking-wider text-white leading-none">
              Lily Admin
            </p>
            <p className="text-[10px] text-linen/60 mt-1 tracking-wide">
              Painel de gestão
            </p>
          </div>
        )}
        {!isDesktop && (
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto p-1.5 rounded-lg text-linen/50 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Fechar menu"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ path, label, Icon }) => {
          const active = isActive(path);
          return (
            <button
              key={path}
              onClick={() => handleNav(path)}
              title={collapsed && isDesktop ? label : undefined}
              className={[
                'group w-full flex items-center gap-3 rounded-xl text-sm font-semibold',
                'transition-all duration-150 outline-none min-h-[44px]',
                'focus-visible:ring-2 focus-visible:ring-wine focus-visible:ring-offset-1 focus-visible:ring-offset-ink',
                collapsed && isDesktop
                  ? 'justify-center px-0 py-3'
                  : 'px-3 py-3',
                active
                  ? 'bg-wine text-white shadow-sm'
                  : 'text-linen/70 hover:bg-white/10 hover:text-blush-light',
              ].join(' ')}
            >
              <Icon
                size={18}
                className={`flex-shrink-0 transition-colors ${active ? 'text-white' : 'text-linen/50 group-hover:text-blush-light'}`}
              />
              {(!collapsed || !isDesktop) && (
                <span className="truncate">{label}</span>
              )}
              {active && (!collapsed || !isDesktop) && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        className={`p-3 border-t border-white/10 space-y-1 ${collapsed && isDesktop ? 'flex flex-col items-center gap-1 space-y-0' : ''}`}
      >
        {isDesktop && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            className={[
              'group flex items-center gap-3 w-full rounded-xl py-3 text-sm font-semibold min-h-[44px]',
              'text-linen/50 hover:text-blush-light hover:bg-white/10 transition-all',
              'focus-visible:ring-2 focus-visible:ring-wine outline-none',
              collapsed ? 'justify-center px-0' : 'px-3',
            ].join(' ')}
          >
            {collapsed ? (
              <ChevronRight size={16} className="flex-shrink-0" />
            ) : (
              <>
                <ChevronLeft size={16} className="flex-shrink-0" />
                <span>Recolher</span>
              </>
            )}
          </button>
        )}
        <button
          onClick={handleLogout}
          title={collapsed && isDesktop ? 'Sair' : undefined}
          className={[
            'group flex items-center gap-3 w-full rounded-xl py-3 text-sm font-semibold min-h-[44px]',
            'text-linen/50 hover:text-red-300 hover:bg-red-500/10 transition-all',
            'focus-visible:ring-2 focus-visible:ring-red-400 outline-none',
            collapsed && isDesktop ? 'justify-center px-0' : 'px-3',
          ].join(' ')}
        >
          <LogOut size={16} className="flex-shrink-0" />
          {(!collapsed || !isDesktop) && <span>Sair</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir menu"
        className="lg:hidden fixed top-4 left-4 z-50 w-11 h-11 flex items-center justify-center rounded-xl bg-wine text-white shadow-lg shadow-wine/30 hover:bg-wine-deep active:scale-95 transition-all"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      <div
        onClick={() => setMobileOpen(false)}
        className={`lg:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      />

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-ink shadow-2xl transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        aria-label="Menu de navegação"
      >
        <SidebarContent isDesktop={false} />
      </aside>

      {/* Desktop spacer — keeps content pushed right */}
      <div
        className="hidden lg:block flex-shrink-0 transition-all duration-300"
        style={{ width: collapsed ? 72 : 240 }}
      />

      {/* Desktop fixed sidebar */}
      <aside
        className="hidden lg:flex flex-col fixed inset-y-0 left-0 bg-ink border-r border-white/10 shadow-sm z-30 transition-all duration-300 overflow-hidden"
        style={{ width: collapsed ? 72 : 240 }}
        aria-label="Menu de navegação"
      >
        <SidebarContent isDesktop={true} />
      </aside>
    </>
  );
}
